/**
 * Service: consumer support surface.
 *
 * Boundaries this layer keeps:
 *   1. The assistant is an EDGE FUNCTION call. No model call, prompt or
 *      retrieval logic exists client-side.
 *   2. access_token is passed only as an RPC argument, never placed in a URL
 *      and never logged. It never leaves this module except to the store.
 *   3. Failure is DATA, not an exception. The assistant maps rate_limited /
 *      credits_exhausted / upstream to outcome="escalated" with a ticket_draft;
 *      callers render that, they do not retry silently.
 */
import { supabase } from "@/integrations/supabase/client";
import type { SupportCategory } from "./support-articles";
import type { SupportPriority, SupportStatus, SupportAuthor } from "./support-tickets";

export type { SupportPriority, SupportStatus, SupportAuthor };

export interface Citation {
  slug: string;
  title: string;
}

export interface AssistantTicket {
  id: string;
  /** Present for anonymous callers only. BEARER SECRET. */
  access_token: string | null;
  priority: SupportPriority;
  status: SupportStatus;
}

export interface AssistantEscalation {
  reason: string;
  /** false when the assistant itself broke (rate limit, credits, upstream). */
  assistant_available: boolean;
  /**
   * true for an ordinary decline (no_articles, model_escalated): the assistant
   * still works and the conversation must stay open. The handoff is offered, not
   * imposed. false for an outage or a created ticket.
   */
  soft?: boolean;
  customer_message: string;
  ticket_created: boolean;
  ticket: AssistantTicket | null;
  /** true when an anonymous caller must supply an email before a ticket exists. */
  needs_email: boolean;
  ticket_error: string | null;

  ticket_draft: {
    category: SupportCategory;
    subject: string;
    body: string;
    /** Operator-only; the edge function strips it before the browser sees it. */
    internal_note?: string;
  } | null;
}

export type AssistantReply =
  | { ok: true; outcome: "answered"; answer: string; citations: Citation[] }
  | { ok: true; outcome: "escalated"; escalation: AssistantEscalation; citations: Citation[] }
  | { ok: false; error: string };

export interface AssistantTurn {
  role: "user" | "assistant";
  content: string;
}

/**
 * Ask the assistant. A transport failure is returned as ok:false so the caller
 * can offer the manual ticket path — a dead spinner is never acceptable.
 */
export async function askAssistant(input: {
  message: string;
  history: AssistantTurn[];
  requesterEmail?: string | null;
  requesterName?: string | null;
}): Promise<AssistantReply> {
  try {
    const { data, error } = await supabase.functions.invoke("support-assistant", {
      body: {
        message: input.message,
        history: input.history,
        requester_email: input.requesterEmail ?? null,
        requester_name: input.requesterName ?? null,
      },
    });
    if (error) return { ok: false, error: error.message || "unreachable" };
    return data as AssistantReply;
  } catch (e) {
    return { ok: false, error: (e as Error).message || "unreachable" };
  }
}

/* ── Direct ticket creation (assistant bypass / manual path) ── */

export async function createTicket(input: {
  subject: string;
  body: string;
  requesterEmail?: string | null;
  requesterName?: string | null;
  category?: SupportCategory | null;
  transcript?: AssistantTurn[];
}): Promise<{ data: AssistantTicket | null; error: string | null }> {
  const { data, error } = await supabase.rpc("create_support_ticket", {
    _subject: input.subject.slice(0, 200),
    _body: input.body,
    _requester_email: input.requesterEmail ?? null,
    _category: input.category ?? "other",
    _requester_name: input.requesterName ?? null,
    _source: "chatbot",
    _ai_handled: false,
    _priority_hint: null,
    _transcript: (input.transcript ?? []) as unknown as never,
  });
  if (error) return { data: null, error: error.message };
  const row = (Array.isArray(data) ? data[0] : data) as
    | { o_ticket_id: string; o_access_token: string | null; o_priority: SupportPriority; o_status: SupportStatus }
    | undefined;
  if (!row) return { data: null, error: "no_row_returned" };
  // A signed-in caller reaches the thread through auth.uid(). The bearer token
  // has no purpose for them, so it is dropped here rather than carried around.
  const { data: sess } = await supabase.auth.getSession();
  return {
    data: {
      id: row.o_ticket_id,
      access_token: sess.session ? null : row.o_access_token,
      priority: row.o_priority,
      status: row.o_status,
    },
    error: null,
  };
}

/* ── Thread read / reply ── */

export interface ThreadMessage {
  id: string;
  author_kind: SupportAuthor;
  body: string;
  created_at: string;
}

export interface Thread {
  id: string;
  subject: string;
  category: SupportCategory | null;
  priority: SupportPriority;
  status: SupportStatus;
  ai_handled: boolean;
  requester_email: string | null;
  requester_name: string | null;
  created_at: string;
  first_agent_response_at: string | null;
  closed_at: string | null;
  messages: ThreadMessage[];
}

export async function getThreadAnon(ticketId: string, accessToken: string) {
  const { data, error } = await supabase.rpc("get_support_ticket_anon", {
    _ticket_id: ticketId,
    _access_token: accessToken,
  });
  return { data: (data as unknown as Thread) ?? null, error };
}

export async function replyAnon(ticketId: string, accessToken: string, body: string) {
  return supabase.rpc("post_support_message_anon", {
    _ticket_id: ticketId,
    _access_token: accessToken,
    _body: body,
  });
}

export async function getMyThread(ticketId: string) {
  const { data, error } = await supabase.rpc("get_my_support_ticket", {
    _ticket_id: ticketId,
  });
  return { data: (data as unknown as Thread) ?? null, error };
}

export async function replyOwn(ticketId: string, body: string) {
  return supabase.rpc("post_support_message", { _ticket_id: ticketId, _body: body });
}

/* ── Signed-in lists ── */

export interface MyTicketRow {
  id: string;
  subject: string;
  category: SupportCategory | null;
  priority: SupportPriority;
  status: SupportStatus;
  ai_handled: boolean;
  created_at: string;
  updated_at: string;
  first_agent_response_at: string | null;
  message_count: number;
  last_message_at: string | null;
}

export async function listMyTickets() {
  const { data, error } = await supabase.rpc("list_my_support_tickets");
  return { data: ((data as unknown as MyTicketRow[]) ?? []), error };
}

export interface ClaimableTicket {
  id: string;
  subject: string;
  status: SupportStatus;
  created_at: string;
}

export async function listClaimableTickets() {
  const { data, error } = await supabase.rpc("list_claimable_support_tickets");
  return { data: ((data as unknown as ClaimableTicket[]) ?? []), error };
}

/** Moves an anonymous ticket onto the account and rotates the bearer token. */
export async function claimTicket(ticketId: string) {
  return supabase.rpc("claim_support_ticket", { _ticket_id: ticketId });
}

/** Emails the private return link to the ticket's own address. Token-gated. */
export async function emailReturnLink(ticketId: string, accessToken: string) {
  const { data, error } = await supabase.rpc("send_support_ticket_return_link", {
    _ticket_id: ticketId,
    _access_token: accessToken,
  });
  return { data: data as unknown as { sent: boolean; reason?: string } | null, error };
}

/* ── Labels ── */

export const THREAD_STATUS_LABEL: Record<SupportStatus, string> = {
  open: "Open",
  awaiting_requester: "Waiting on you",
  awaiting_agent: "With our team",
  resolved: "Resolved",
  closed: "Closed",
};

export const AUTHOR_LABEL: Record<SupportAuthor, string> = {
  requester: "You",
  agent: "Cardmoji support",
  ai: "Support assistant",
};
